import {
  differenceInDays,
  differenceInMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isThisMonth,
  isThisWeek,
  isThisYear,
  isToday,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { TZDate } from 'react-day-picker';

export type DateSelectedValue =
  | 'Day'
  | 'Week'
  | 'Month'
  | 'Year'
  | 'Custom-date';
export type TDateSelected = {
  start: TZDate;
  end: TZDate;
  type: DateSelectedValue;
  label: string;
};
export const commonKey = {
  whmcsIdKey: 'whmcsId',
  startDateKey: 'startDate',
  endDateKey: 'endDate',
  apiDateFormat: 'yyyy-MM-dd',
  apiDateTimeFormat: 'yyyy-MM-dd HH:mm:ss',
  urlDateTimeFormat: 'yyyy/MM/dd',
  enDateTimeFormat: 'MMM dd, yyyy',
  viDateTimeFormat: 'dd / MM / yyyy',
  selectedDateKey: 'selectedDate',
};
export const dateToPreviousDate = (
  startDate: TZDate,
  endDate: TZDate,
): TDateSelected => {
  const startDay = startOfDay(startDate);
  const endDay = endOfDay(endDate);

  if (isToday(startDay) && isToday(endDay))
    return {
      start: subDays(startDay, 1),
      end: subDays(endDay, 1),
      label: 'yesterday',
      type: 'Day',
    };

  if (
    isSameDay(startOfWeek(startDay, { weekStartsOn: 1 }), startDay) &&
    isSameDay(endOfWeek(endDay, { weekStartsOn: 1 }), endDay) &&
    isThisWeek(startDay, { weekStartsOn: 1 }) &&
    isThisWeek(endDay, { weekStartsOn: 1 })
  ) {
    return {
      start: startOfWeek(subWeeks(startDay, 1), { weekStartsOn: 1 }),
      end: endOfWeek(subWeeks(endDay, 1), { weekStartsOn: 1 }),
      label: 'last week',
      type: 'Week',
    };
  }

  if (
    isSameDay(startOfMonth(startDay), startDay) &&
    isSameDay(endOfMonth(endDay), endDay) &&
    isThisMonth(startDay) &&
    isThisMonth(endDay)
  ) {
    return {
      start: startOfMonth(subMonths(startDay, 1)),
      end: endOfMonth(subMonths(endDay, 1)),
      label: 'last month',
      type: 'Month',
    };
  }

  if (
    isSameDay(startOfYear(startDay), startDay) &&
    isSameDay(endOfYear(endDay), endDay) &&
    isThisYear(startDay) &&
    isThisYear(endDay)
  ) {
    return {
      start: startOfYear(subYears(startDay, 1)),
      end: endOfYear(subYears(endDay, 1)),
      label: 'last year',
      type: 'Year',
    };
  }

  if (isFirstDayOfMonth(startDay) && isLastDayOfMonth(endDay)) {
    const diff = differenceInMonths(endDay, startDay);
    return {
      start: startOfMonth(subMonths(startDay, diff + 1)),
      end: endOfMonth(subMonths(endDay, diff + 1)),
      label: `${diff + 1} months`,
      type: 'Custom-date',
    };
  }

  const diff = differenceInDays(endDate, startDate) + 1;
  const previousStart = subDays(startDay, diff);
  const previousEnd = subDays(endDay, diff);
  return {
    start: previousStart,
    end: previousEnd,
    label: `${diff} days`,
    type: 'Custom-date',
  };
};

export const parseApiDateTimeString = (dateTimeString: string) => {
  return parseISO(dateTimeString);
};

export const parseUrlDateString = (dateString: string) => {
  return parse(dateString, commonKey.urlDateTimeFormat, new TZDate(new Date()));
};

export const formatVisaExpiry = (expiryDate: string): string => {
  if (expiryDate.length !== 4 || Number.isNaN(Number(expiryDate))) {
    throw new Error("Invalid expiry date format. Expected 'YYMM'.");
  }

  const yearPrefix = parseInt(expiryDate.slice(0, 2), 10);
  const month = expiryDate.slice(2);

  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const fullYear = currentCentury + yearPrefix;

  const adjustedYear = fullYear < currentYear - 50 ? fullYear + 100 : fullYear;

  return `${adjustedYear}${month}`;
};

export const transactionsType = {
  REDEMPTION: 'REDEMPTION',
  TOP_UP: 'TOP_UP',
};

export const giftCardStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  USED_UP: 'used_up',
  INITIAL: 'initial',
};

export const paymentMethods = {
  CASH: 'cash',
  CHARGE: 'charge',
  GIFT_CARD: 'gift_card',
  MANUAL_CARD_ENTRY: 'manual_card_entry',
};

export const Sound = {
  TAP: '/sounds/key.wav',
  PAYMENT_SUCCESS: '/sounds/payment-success.mp3',
  PAYMENT_FAILED: '/sounds/payment-fail.mp3',
  REMOVE_CARD: '/sounds/remove-card.mp3',
  INSERT_CARD: '/sounds/insert-card.mp3',
  PAYMENT_SUCCESS_AMEX: '/sounds/payment-success-amex.mp3',
  PAYMENT_SUCCESS_DISCOVER: '/sounds/payment-success-discover.mp3',
  PAYMENT_SUCCESS_VISA: '/sounds/payment-success-visa.mp3',
  PAYMENT_SUCCESS_MASTERCARD: '/sounds/payment-success-mastercard.mp3',
};

export const SuccessLottiePath = {
  PAYMENT_SUCCESS_AMEX: '/images/lotties/successful-amex.json',
  PAYMENT_SUCCESS_DISCOVER: '/images/lotties/successful-discover.json',
  PAYMENT_SUCCESS_VISA: '/images/lotties/successful-visa.json',
  PAYMENT_SUCCESS_MASTERCARD: '/images/lotties/successful-mastercard.json',
};

export const CardType = {
  VISA: 'visa',
  MASTERCARD: 'mastercard',
  MAESTRO: 'maestro',
  DISCOVER: 'discover',
  JCB: 'jcb',
  DINERS: 'diners',
  CUP: 'cup',
  AMERICAN: 'american',
};
